import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { Header } from "@/components/site/Header";
import { Footer, waLink } from "@/components/site/Footer";
import { MessageCircle } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { Spinner } from "@/components/site/Bits";
import Home from "@/pages/Home";
import { Stays, Vehicles } from "@/pages/Listings";
import ListingDetail from "@/pages/ListingDetail";
import MyBookings from "@/pages/MyBookings";
import { Services, Tours, Transfers } from "@/pages/Services";
import { ComingSoon } from "@/pages/ComingSoon";
import { About, Reviews } from "@/pages/About";
import Support from "@/pages/Support";
import Investors from "@/pages/Investors";
import { Policies, PolicyDetail } from "@/pages/Policies";
import { Login, Register, ForgotPassword, ResetPassword } from "@/pages/Auth";
import Account from "@/pages/Account";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import Partner, { PartnerApply } from "@/pages/Partner";
import PartnerPortal from "@/pages/PartnerPortal";
import Shipping from "@/pages/Shipping";
import ShipmentTrack from "@/pages/ShipmentTrack";
import Estates from "@/pages/Estates";
import { GoogleGate } from "@/components/site/GoogleAuth";
import { AssistantWidget } from "@/components/site/Assistant";

const Admin = lazy(() => import("@/admin/AdminApp"));

const TITLES: [RegExp, string][] = [[/^\/$/, ""], [/^\/stays/, "Stays in Accra"], [/^\/vehicles/, "Vehicle Collection"], [/^\/tours/, "Tours & Experiences"],
  [/^\/airport-transfers/, "Airport Pickups"], [/^\/services/, "Services"], [/^\/estates/, "Gant Estates (Proposed)"], [/^\/shipping/, "Cargo & International Shipping (Coming 2027)"], [/^\/coming-soon/, "Coming Soon"],
  [/^\/partner\/portal/, "Partner Portal"], [/^\/partner/, "Partner With Us"], [/^\/about/, "About the Founder"], [/^\/reviews/, "Guest Reviews"],
  [/^\/support/, "Customer Support"], [/^\/policies/, "Policies"], [/^\/my-bookings/, "My Bookings"], [/^\/admin/, "Admin"], [/^\/(login|register|account)/, "Account"]];

const ScrollTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
    const t = TITLES.find(([r]) => r.test(pathname))?.[1];
    document.title = t ? `${t} | Trust Logistics LLC` : "Trust Logistics LLC — Delivering Trust.";
  }, [pathname]);
  return null;
};

const WhatsAppFloat = () => {
  const { s } = useSettings();
  const p = s?.phones?.find((x) => x.label.toLowerCase().includes("united states")) || s?.phones?.[0];
  if (!p) return null;
  return (
    <a href={waLink(p)} target="_blank" rel="noreferrer" data-testid="whatsapp-float-button" aria-label={`Chat with Trust Logistics on WhatsApp (${p.number})`}
      className="fixed bottom-6 right-6 z-40 inline-flex h-14 w-14 items-center justify-center rounded-full bg-gold text-white shadow-lg shadow-gold/30 transition-transform hover:scale-105">
      <MessageCircle size={24} />
    </a>
  );
};

const PublicLayout = () => (
  <div className="flex min-h-screen flex-col">
    <Header />
    <main className="flex-1"><Outlet /></main>
    <Footer />
    <WhatsAppFloat />
    <AssistantWidget />
  </div>
);

const StaffOnly = () => {
  const { user, isStaff } = useAuth();
  if (user === null) return <Spinner />;
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (!isStaff) return <Navigate to="/account" replace />;
  return <Suspense fallback={<Spinner />}><Admin /></Suspense>;
};

export default function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <BrowserRouter>
          <ScrollTop />
          <GoogleGate>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/stays" element={<Stays />} />
              <Route path="/stays/:id" element={<ListingDetail kind="stay" />} />
              <Route path="/vehicles" element={<Vehicles />} />
              <Route path="/vehicles/:id" element={<ListingDetail kind="vehicle" />} />
              <Route path="/tours" element={<Tours />} />
              <Route path="/airport-transfers" element={<Transfers />} />
              <Route path="/coming-soon" element={<ComingSoon />} />
              <Route path="/gant-estate" element={<Navigate to="/estates" replace />} />
              <Route path="/estates" element={<Estates />} />
              <Route path="/cars" element={<Navigate to="/vehicles" replace />} />
              <Route path="/contact" element={<Navigate to="/support" replace />} />
              <Route path="/shipping" element={<Shipping />} />
              <Route path="/shipping/track" element={<ShipmentTrack />} />
              <Route path="/partner" element={<Partner />} />
              <Route path="/partner/portal" element={<PartnerPortal />} />
              <Route path="/partner/:slug" element={<PartnerApply />} />
              <Route path="/about" element={<About />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/support" element={<Support />} />
              <Route path="/policies" element={<Policies />} />
              <Route path="/policies/:slug" element={<PolicyDetail />} />
              <Route path="/privacy" element={<Navigate to="/policies/privacy" replace />} />
              <Route path="/terms" element={<Navigate to="/policies/terms" replace />} />
              <Route path="/investors" element={<Investors />} />
              <Route path="/my-bookings" element={<MyBookings />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/account" element={<Account />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
            <Route path="/admin/*" element={<StaffOnly />} />
          </Routes>
          </GoogleGate>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </SettingsProvider>
    </AuthProvider>
  );
}
